import { sortFormattedDatasets } from 'components_new/molecules/Table/utils'

import {
  FormattedWidgetData,
  FormattedWidgetDataDataset,
  ParsedWidgetDataLabel,
  TWidgetSettings,
  WidgetType
} from 'types/GlobalWidget'

import { hasVariantOptions } from 'utils/variantOptions'

/**
 * Rows of a hierarchical table are indented by their depth, so the
 * structure survives the export. Metric rows re-parent the account
 * hierarchy, which makes the same label appear under several
 * metrics — without the indentation those rows are indistinguishable.
 */
function getExportLabel(
  label: ParsedWidgetDataLabel,
  isHierarchical: boolean
): string {
  if (!isHierarchical || label.path.length === 0) {
    return label.display_label
  }

  return `${'  '.repeat(label.path.length)}${label.display_label}`
}

/**
 * Convert widget data to export format (headers + rows)
 */
export function convertWidgetDataToExport(
  settings: TWidgetSettings,
  formattedData: FormattedWidgetData,
  totalLabel: string,
  variantOptionsLabel: string
) {
  const { labels } = formattedData

  const isHierarchical =
    settings.type.selected === WidgetType.LIST &&
    labels.some((label) => label.path.length > 0)

  // Sort and hide columns according to tables
  const datasets =
    settings.type.selected === WidgetType.LIST
      ? sortFormattedDatasets(formattedData.datasets)
      : formattedData.datasets

  // with variant options the rows are metrics, not segment values
  const variantOptionRows =
    formattedData.expandableMetricRows && hasVariantOptions(settings)

  const segmentByDisplayHeader = variantOptionRows
    ? settings.segment_by.display_name ?? variantOptionsLabel
    : settings.segment_by.display_name ?? settings.segment_by.label

  const segmentByHeader =
    settings.type.selected === WidgetType.LINE_CHART
      ? 'Datum'
      : segmentByDisplayHeader

  // the total column is not a segment, so it is named after what it
  // holds rather than after a value it was measured for
  const headerOf = (dataset: FormattedWidgetDataDataset) =>
    dataset.isTotal ? totalLabel : dataset.label

  if (segmentByHeader) {
    const headers = [
      segmentByHeader,
      ...datasets.map((dataset) => {
        const unit = dataset.unit ? ` (${dataset.unit})` : ''

        return headerOf(dataset) + unit
      })
    ]

    const rows: { [key: string]: string | number }[] = []

    labels.forEach((label, index) => {
      const currentRow: { [key: string]: string | number } = {
        [segmentByHeader]: getExportLabel(label, isHierarchical)
      }

      datasets.forEach((dataset) => {
        const value = dataset.data[index]

        currentRow[headerOf(dataset)] = Array.isArray(value)
          ? value.join(',')
          : value ?? 0
      })

      rows.push(currentRow)
    })

    return { rows, headers }
  }

  return { rows: [], headers: [] }
}
